import { useMemo } from "react";
import { AlertTriangle, CheckCircle2, Circle, Trash2 } from "lucide-react";
import { useConfigStore, countMissingLightDark } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { validateSkinNames } from "../../lib/spineParser";
import { Button } from "@/components/ui/button";

interface ValidationItem {
  id: string;
  label: string;
  passed: boolean;
  detail?: string;
}

export function ConfigValidation() {
  const {
    spineData,
    templateConfig,
    parsedSkins,
  } = useConfigStore();
  const {
    getSkuErrors,
    removeOrphanSkus,
  } = useEditorStore();

  // 配置完整性检查
  const validationItems = useMemo<ValidationItem[]>(() => {
    if (!spineData || !templateConfig) return [];

    const items: ValidationItem[] = [];
    const slots =
      spineData.json?.skeleton?.slots || spineData.json?.slots || [];
    const skins = spineData.json?.skins || [];

    // 构建 slot -> skins 映射
    const skinsBySlot = new Map<string, { name: string }[]>();
    for (const skin of skins) {
      if (!skin.attachments) continue;
      for (const slotName of Object.keys(skin.attachments)) {
        if (!skinsBySlot.has(slotName)) {
          skinsBySlot.set(slotName, []);
        }
        const existing = skinsBySlot.get(slotName)!;
        if (!existing.some((s) => s.name === skin.name)) {
          existing.push({ name: skin.name });
        }
      }
    }

    // 构建 skinToSelection 映射
    const skinToSelection = new Map<string, boolean>();
    const skus = templateConfig.skus || {};
    for (const selection of templateConfig.selection || []) {
      const skuIds = selection.skuIds || [];
      for (const skuId of skuIds) {
        const sku = skus[skuId];
        if (sku?.skinName) {
          skinToSelection.set(sku.skinName, true);
        }
      }
    }

    // 1. 检查空白 Slot
    const emptySlots = slots.filter((slot: any) => {
      const slotSkins = skinsBySlot.get(slot.name) || [];
      return slotSkins.length === 0;
    });
    items.push({
      id: "empty-slots",
      label: "[spine]Slot 均有 Skin",
      passed: emptySlots.length === 0,
      detail:
        emptySlots.length > 0
          ? `${emptySlots.length} 个空 Slot: ${emptySlots.map((s: any) => s.name).join(", ")}`
          : undefined,
    });

    // 2. 检查 Skin 绑定
    const unboundSlots: string[] = [];
    slots.forEach((slot: any) => {
      const slotSkins = skinsBySlot.get(slot.name) || [];
      const hasUnbound = slotSkins.some(
        (skin) => !skinToSelection.has(skin.name)
      );
      if (slotSkins.length > 0 && hasUnbound) {
        unboundSlots.push(slot.name);
      }
    });
    items.push({
      id: "skin-bindings",
      label: "[spine]Skin 均已绑定",
      passed: unboundSlots.length === 0,
      detail:
        unboundSlots.length > 0
          ? `${unboundSlots.length} 个 Slot 有未绑定 Skin: ${unboundSlots.join(", ")}`
          : undefined,
    });

    // 3. Skin Name 规范
    const skinNameErrors = validateSkinNames(spineData.json);
    items.push({
      id: "skin-name-valid",
      label: "[spine]Skin Name 规范",
      passed: skinNameErrors.length === 0,
      detail:
        skinNameErrors.length > 0
          ? `${skinNameErrors.length} 个不合规: ${skinNameErrors
              .slice(0, 3)
              .map((e) => e.skinName)
              .join(", ")}${skinNameErrors.length > 3 ? "..." : ""}`
          : "2-3层 / 无特殊符号",
    });

    // 3. 必选 Category
    const requireCategories = templateConfig.requireCategories || [];
    const categories = new Set(parsedSkins.map((s) => s.category));
    items.push({
      id: "require-categories",
      label: "已设置必选 Category",
      passed: requireCategories.length > 0 || categories.size === 0,
      detail:
        requireCategories.length === 0 && categories.size > 0
          ? "随机换装可能为空"
          : requireCategories.length > 0
            ? `${requireCategories.length} 个必选`
            : undefined,
    });

    // 4. Selection 图标
    const selections = templateConfig.selection || [];
    const noCoverSelections = selections.filter((sel: any) => !sel.layerIconId);
    items.push({
      id: "selection-covers",
      label: "Selection 均有图标",
      passed: noCoverSelections.length === 0,
      detail:
        noCoverSelections.length > 0
          ? `${noCoverSelections.length} 个缺少图标: ${noCoverSelections.map((s: any) => s.name || s.id).join(", ")}`
          : undefined,
    });

    // 5. 孤儿 SKU
    const validSkuIds = new Set(parsedSkins.map((s) => s.skuId));
    const orphanSkus: { skuId: string; skinName: string }[] = [];
    const allSkus = templateConfig.skus || {};
    for (const selection of selections) {
      const selSkuIds = selection.skuIds || [];
      for (const skuId of selSkuIds) {
        const sku = allSkus[skuId];
        if (sku && !validSkuIds.has(skuId)) {
          orphanSkus.push({ skuId: skuId, skinName: sku.skinName || "" });
        }
      }
    }
    items.push({
      id: "orphan-skus",
      label: "SKU 均有对应 Skin",
      passed: orphanSkus.length === 0,
      detail:
        orphanSkus.length > 0
          ? `${orphanSkus.length} 个无效 SKU: ${orphanSkus.map((o) => o.skinName).join(", ")}`
          : undefined,
    });

    // 6. 空 Selection
    const emptySelections = selections.filter(
      (sel: any) => !sel.skuIds || sel.skuIds.length === 0
    );
    items.push({
      id: "empty-selections",
      label: "Selection 均有 SKU",
      passed: emptySelections.length === 0,
      detail:
        emptySelections.length > 0
          ? `${emptySelections.length} 个空 Selection: ${emptySelections.map((s: any) => s.name || s.id).join(", ")}`
          : undefined,
    });

    // 7. Toning 绑定
    const tonings = templateConfig.tonings || [];
    const usedToningIds = new Set<string>();
    for (const config of Object.values(skus) as any[]) {
      if (config.toningIds) {
        for (const tid of config.toningIds) {
          usedToningIds.add(tid);
        }
      }
    }
    const unboundTonings = tonings.filter((t: any) => !usedToningIds.has(t.id));
    items.push({
      id: "unbound-tonings",
      label: "Toning 均已绑定",
      passed: unboundTonings.length === 0 || tonings.length === 0,
      detail:
        unboundTonings.length > 0
          ? `${unboundTonings.length} 个未绑定: ${unboundTonings.map((t: any) => t.name || t.id).join(", ")}`
          : undefined,
    });

    // 8. 配色 light/dark 完整性（使用集中校验方法）
    const toningsWithMissingColors: { name: string; count: number }[] = [];
    for (const toning of tonings) {
      const missingCount = countMissingLightDark(toning);
      if (missingCount > 0) {
        toningsWithMissingColors.push({
          name: toning.name || toning.id,
          count: missingCount,
        });
      }
    }
    items.push({
      id: "toning-colors-complete",
      label: "配色 light/dark 完整",
      passed: toningsWithMissingColors.length === 0 || tonings.length === 0,
      detail:
        toningsWithMissingColors.length > 0
          ? `${toningsWithMissingColors.length} 个 Toning 有缺失: ${toningsWithMissingColors
              .slice(0, 3)
              .map((t) => `${t.name}(${t.count})`)
              .join(", ")}${toningsWithMissingColors.length > 3 ? "..." : ""}`
          : undefined,
    });

    // 9. SKU Toning 数据完整性
    const skusWithToningErrors: { skuId: string; errors: string[] }[] = [];
    for (const skuId of Object.keys(skus)) {
      const errors = getSkuErrors(skuId);
      const toningErrors = errors.filter(
        (e) => e.type === "invalid_toning" || e.type === "duplicate_toning"
      );
      if (toningErrors.length > 0) {
        skusWithToningErrors.push({
          skuId,
          errors: toningErrors.map((e) => e.message),
        });
      }
    }
    items.push({
      id: "sku-toning-valid",
      label: "SKU Toning 数据完整",
      passed: skusWithToningErrors.length === 0,
      detail:
        skusWithToningErrors.length > 0
          ? `${skusWithToningErrors.length} 个 SKU 有问题: ${skusWithToningErrors
              .slice(0, 3)
              .map((s) => s.errors.join(", "))
              .join("; ")}${skusWithToningErrors.length > 3 ? "..." : ""}`
          : undefined,
    });

    // 10. 隐藏 SKU 不应有价格
    const hiddenSkusWithPrice: string[] = [];
    for (const [skuId, sku] of Object.entries(allSkus) as [string, any][]) {
      if (sku.hidden && sku.pro > 0) {
        hiddenSkusWithPrice.push(sku.skinName || skuId);
      }
    }
    items.push({
      id: "hidden-sku-no-price",
      label: "隐藏 SKU 无价格",
      passed: hiddenSkusWithPrice.length === 0,
      detail:
        hiddenSkusWithPrice.length > 0
          ? `${hiddenSkusWithPrice.length} 个隐藏 SKU 有价格: ${hiddenSkusWithPrice
              .slice(0, 3)
              .join(", ")}${hiddenSkusWithPrice.length > 3 ? "..." : ""}`
          : undefined,
    });

    // 11. 配色无冗余旧数据（同时有 colors 和 light/dark）
    const colorSetsWithLegacyData: { toningName: string; count: number }[] = [];
    for (const toning of tonings) {
      let legacyCount = 0;
      for (const colorSet of toning.colors || []) {
        const hasLegacy = colorSet.colors && colorSet.colors.length > 0;
        const hasNew = colorSet.light && colorSet.dark;
        if (hasLegacy && hasNew) {
          legacyCount++;
        }
      }
      if (legacyCount > 0) {
        colorSetsWithLegacyData.push({
          toningName: toning.name || toning.id,
          count: legacyCount,
        });
      }
    }
    items.push({
      id: "colorset-no-legacy",
      label: "配色无冗余旧数据",
      passed: colorSetsWithLegacyData.length === 0 || tonings.length === 0,
      detail:
        colorSetsWithLegacyData.length > 0
          ? `${colorSetsWithLegacyData.length} 个 Toning 有冗余: ${colorSetsWithLegacyData
              .slice(0, 3)
              .map((t) => `${t.toningName}(${t.count})`)
              .join(", ")}${colorSetsWithLegacyData.length > 3 ? "..." : ""}`
          : undefined,
    });

    // 12. SKU 均有 pro 价格
    const skusWithoutPro: string[] = [];
    for (const [skuId, sku] of Object.entries(allSkus) as [string, any][]) {
      if (sku.pro === undefined) {
        skusWithoutPro.push(sku.skinName || skuId);
      }
    }
    items.push({
      id: "sku-has-pro",
      label: "SKU 均有 pro 价格",
      passed: skusWithoutPro.length === 0,
      detail:
        skusWithoutPro.length > 0
          ? `${skusWithoutPro.length} 个 SKU 缺少 pro: ${skusWithoutPro
              .slice(0, 3)
              .join(", ")}${skusWithoutPro.length > 3 ? "..." : ""}`
          : undefined,
    });

    // 13. Toning 均有 pro 价格
    const toningsWithoutPro: string[] = [];
    for (const toning of tonings) {
      if (toning.pro === undefined) {
        toningsWithoutPro.push(toning.name || toning.id);
      }
    }
    items.push({
      id: "toning-has-pro",
      label: "Toning 均有 pro 价格",
      passed: toningsWithoutPro.length === 0 || tonings.length === 0,
      detail:
        toningsWithoutPro.length > 0
          ? `${toningsWithoutPro.length} 个 Toning 缺少 pro: ${toningsWithoutPro
              .slice(0, 3)
              .join(", ")}${toningsWithoutPro.length > 3 ? "..." : ""}`
          : undefined,
    });

    return items;
  }, [spineData, templateConfig, parsedSkins, getSkuErrors]);

  const passedCount = validationItems.filter((item) => item.passed).length;
  const failedCount = validationItems.filter((item) => !item.passed).length;

  if (validationItems.length === 0) return null;

  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-3 h-9 text-sm group relative cursor-help ${
        failedCount > 0
          ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400"
          : "bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400"
      }`}
    >
      {failedCount > 0 ? (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      ) : (
        <CheckCircle2 className="h-4 w-4 shrink-0" />
      )}
      <span className="font-medium">
        {failedCount > 0
          ? `${passedCount}/${validationItems.length} 通过`
          : "配置完整"}
      </span>

      {/* 悬浮显示详情 */}
      <div className="absolute top-full right-0 mt-2 w-96 max-h-80 overflow-y-auto bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
        <div className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-3">
          配置检查清单
        </div>
        <ul className="space-y-2.5">
          {validationItems.map((item) => (
            <li key={item.id} className="flex items-start gap-2">
              {item.passed ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
              ) : (
                <Circle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div
                  className={`text-xs font-medium ${item.passed ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}
                >
                  {item.label}
                </div>
                {item.detail && (
                  <div
                    className={`text-xs mt-0.5 break-words ${item.passed ? "text-emerald-600/70 dark:text-emerald-400/70" : "text-amber-600/80 dark:text-amber-400/80"}`}
                  >
                    {item.detail}
                  </div>
                )}
                {/* 无效 SKU 的清理按钮 */}
                {item.id === "orphan-skus" && !item.passed && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-6 text-[10px] mt-1.5 gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      const count = removeOrphanSkus();
                      alert(`已移除 ${count} 个无效 SKU`);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                    批量移除
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
